import React, { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Newspaper, Loader2, Download, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const getCleanUrl = (url) => {
  if (!url) return '';
  if (url.includes('http')) return url;
  const backend = process.env.REACT_APP_BACKEND_URL || '';
  const cleanBackend = backend.endsWith('/') ? backend.slice(0, -1) : backend;
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;
  return `${cleanBackend}${cleanUrl}`;
};

const PDFThumbnail = ({ pdfUrl, title = 'Kallinakoogu Newspaper' }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const fullUrl = getCleanUrl(pdfUrl);

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleDownload = async () => {
    try {
      const response = await fetch(fullUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `Kallinakoogu-Edition.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      window.open(fullUrl, '_blank');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Kallinakoogu',
          text: `ಇಂದಿನ ಕಲ್ಲಿನಕೂಗು ಪತ್ರಿಕೆ ಓದಿ:`,
          url: fullUrl,
        });
      } catch (err) {
        console.log('Share failed', err);
      }
    } else {
      navigator.clipboard.writeText(fullUrl);
      alert('Link copied to clipboard!');
    }
  };

  const thumbWidth = screenWidth > 768 ? 800 : screenWidth - 48;

  return (
    <div className="relative w-full h-auto min-h-[400px] bg-white rounded-xl overflow-hidden flex flex-col items-center border border-gray-200 shadow-md mb-6">
      
      {/* ACTION BAR */}
      <div className="w-full bg-gray-50 border-b p-3 flex justify-end space-x-2 z-20">
        <Button variant="outline" size="sm" onClick={handleShare} className="flex items-center gap-2 border-gray-300">
          <Share2 className="h-4 w-4" /> Share
        </Button>
        <Button size="sm" onClick={handleDownload} className="bg-red-700 hover:bg-red-800 text-white flex items-center gap-2">
          <Download className="h-4 w-4" /> Download
        </Button>
      </div>

      {loading && !error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 z-10 pt-10">
          <Loader2 className="h-10 w-10 text-red-700 animate-spin mb-2" />
          <p className="text-sm text-gray-500 font-medium font-kannada">ಪತ್ರಿಕೆಯನ್ನು ಸಿದ್ಧಪಡಿಸಲಾಗುತ್ತಿದೆ...</p>
        </div>
      )}
      
      {error && (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <Newspaper className="h-16 w-16 mb-2" />
          <p className="text-sm">ಪತ್ರಿಕೆ ಲಭ್ಯವಿಲ್ಲ</p>
        </div>
      )}

      {!error && (
        <Document
          file={fullUrl}
          onLoadSuccess={() => { setLoading(false); setError(false); }}
          onLoadError={() => { setLoading(false); setError(true); }}
          className="flex flex-col items-center p-2"
        >
          <Page
            pageNumber={1}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            width={thumbWidth}
            className="shadow-lg border border-gray-100"
          />
        </Document>
      )}
    </div>
  );
};

export default PDFThumbnail;
